import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  OpenInFull as OpenInFullIcon,
  RemoveRedEye as EyeIcon,
  DeleteOutline as DisposalIcon,
  Shield as PPEIcon,
  Inventory2 as StorageIcon,
  WaterDrop as SpillIcon,
} from '@mui/icons-material';
import { FileFullscreenViewer } from '../../../../files/components/FileFullscreenViewer';
import HazardDot, { hazardLevelText } from '../../../components/HazardDot';

function StatusChip({ msds, t }) {
  if (!msds.is_valid) {
    return <Chip icon={<ErrorIcon />} label={t('inventory:msds.tab.status_chip.invalid')} color="error" size="small" />;
  }
  if (msds.is_expired) {
    return <Chip icon={<ErrorIcon />} label={t('inventory:msds.tab.status_chip.expired')} color="error" size="small" />;
  }
  if (msds.is_expiring_soon) {
    return (
      <Chip
        icon={<WarningIcon />}
        label={t('inventory:msds.tab.status_chip.expiring_soon', { days: msds.days_until_review })}
        color="warning"
        size="small"
      />
    );
  }
  return <Chip icon={<CheckIcon />} label={t('inventory:msds.tab.status_chip.up_to_date')} color="success" size="small" />;
}

function SafetyCard({ title, content, icon: Icon, color, emptyLabel }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        {Icon && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 1, bgcolor: `${color}.50`, color: `${color}.main`, flexShrink: 0 }}>
            <Icon sx={{ fontSize: 16 }} />
          </Box>
        )}
        <Typography variant="subtitle2" fontWeight={600}>
          {title}
        </Typography>
      </Box>
      <Typography variant="body2" color={content ? 'text.secondary' : 'text.disabled'} fontStyle={content ? 'normal' : 'italic'} sx={{ pl: Icon ? '36px' : 0, whiteSpace: 'pre-wrap' }}>
        {content || emptyLabel}
      </Typography>
    </Paper>
  );
}

export default function MsdsDrawer({ open, msds, msdsLoading, canOpenFile = true, onClose }) {
  const { t } = useTranslation(['inventory', 'common']);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);

  const emptyLabel = t('inventory:msds.tab.empty');

  return (
    <>
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { borderRadius: '16px 16px 0 0', p: 3, pt: 2, pb: 5, maxHeight: '85vh', overflow: 'auto' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6">
            {t('inventory:scanner.msds_drawer_title')}
          </Typography>
          <IconButton onClick={onClose} aria-label={t('common:buttons.close')} sx={{ width: 48, height: 48, mr: -1.5 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {msdsLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!msdsLoading && !msds && (
          <Alert severity="info">
            {t('inventory:scanner.msds_not_found')}
          </Alert>
        )}

        {!msdsLoading && msds && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Status row. Language and version are admin detail, left to the MSDS page */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              {msds.hazard_level && (
                <Chip
                  icon={<HazardDot level={msds.hazard_level} tooltip={false} sx={{ ml: 2 }} />}
                  label={hazardLevelText(t, msds.hazard_level)}
                  size="small"
                  variant="outlined"
                />
              )}
              <StatusChip msds={msds} t={t} />
              <Typography variant="caption" color="text.secondary">
                {t('inventory:msds.tab.review_label')} {new Date(msds.review_date).toLocaleDateString('da-DK')}
              </Typography>
            </Box>

            <Divider />

            {/* Safety cards */}
            <SafetyCard title={t('inventory:msds.fields.eye_contact')} content={msds.eye_contact} icon={EyeIcon} color="info" emptyLabel={emptyLabel} />
            <SafetyCard title={t('inventory:msds.fields.disposal')} content={msds.disposal} icon={DisposalIcon} color="error" emptyLabel={emptyLabel} />
            <SafetyCard title={t('inventory:msds.fields.ppe')} content={msds.ppe} icon={PPEIcon} color="success" emptyLabel={emptyLabel} />
            <SafetyCard title={t('inventory:msds.fields.storage')} content={msds.storage} icon={StorageIcon} color="warning" emptyLabel={emptyLabel} />
            <SafetyCard title={t('inventory:msds.fields.spill_handling')} content={msds.spill_handling} icon={SpillIcon} color="primary" emptyLabel={emptyLabel} />

            {msds.file && canOpenFile && (
              <Button
                variant="outlined"
                size="large"
                startIcon={<OpenInFullIcon />}
                onClick={() => setFullscreenOpen(true)}
                fullWidth
                sx={{ minHeight: 52 }}
              >
                {t('inventory:scanner.msds_open_sds')}
              </Button>
            )}
          </Box>
        )}
      </Drawer>

      {fullscreenOpen && msds?.file && (
        <FileFullscreenViewer
          fileId={msds.file.file_id}
          filename={msds.file.filename}
          filetype={msds.file.filetype}
          onClose={() => setFullscreenOpen(false)}
        />
      )}
    </>
  );
}
